import {
  Module,
  VuexModule,
  Action,
  getModule
} from 'vuex-module-decorators'
import store from '@/store'
import { toasts } from '@/utils/toasts'
import i18n from '@/lang'
import { AppModule } from '@/store/modules/app'
import { UserNotifications } from '@/features/user-notifications/api/user-notifications'
import { IUserNotificationResponseModel } from '@/api/typings-generated/user-notification'

export interface IUserNotificationsGetPayload {
  currentPage: number
  perPage: number
  userGuid: string | null
  isRead: boolean | null
  isArchived: boolean | null
  entityNames: string[]
  fromDate: Date | null
  toDate: Date | null
}

@Module({ dynamic: true, store, name: 'UserNotificationsActions' })
class UserNotificationsActions extends VuexModule {
  @Action
  async getUserNotifications(
    payload: { userNotificationsPayload: IUserNotificationsGetPayload, verbose?: boolean})
    : Promise<IUserNotificationResponseModel | false | null> {
    const verbose = payload.verbose ?? true
    try {
      await AppModule.setIsRequestActive(true)
      const response = await UserNotifications.fetchUserNotifications(
        payload.userNotificationsPayload.currentPage,
        payload.userNotificationsPayload.perPage,
        payload.userNotificationsPayload.userGuid,
        payload.userNotificationsPayload.isRead,
        payload.userNotificationsPayload.isArchived,
        payload.userNotificationsPayload.entityNames,
        payload.userNotificationsPayload.fromDate,
        payload.userNotificationsPayload.toDate
      )
      await AppModule.setIsRequestActive(false)

      if (!response.isSuccessful && verbose) {
        toasts.error((i18n.t('userNotifications.toasts.getUserNotificationsError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }

      return response.isSuccessful && response.payload ? response.payload : false
    } catch {
      await AppModule.setIsRequestActive(false)
      if (verbose) {
        toasts.error((i18n.t('userNotifications.toasts.getUserNotificationsServerError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }
      return null
    }
  }

  @Action
  async markNotificationsAsRead(
    payload: { userNotificationIds: number[], isRead: boolean, verbose?: boolean})
    : Promise<boolean | null> {
    const verbose = payload.verbose ?? true
    try {
      await AppModule.setIsRequestActive(true)
      const response = await UserNotifications.markNotificationsAsRead(
        payload.userNotificationIds,
        payload.isRead
      )
      await AppModule.setIsRequestActive(false)

      if ((!response.isSuccessful || !response.payload) && verbose) {
        toasts.error((i18n.t('userNotifications.toasts.updateUserNotificationsError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }

      if (response.isSuccessful && verbose) {
        toasts.success((i18n.t('userNotifications.toasts.updateUserNotificationsSuccessfully')) as string, {
          title: (i18n.t('userNotifications.toasts.successTitle')) as string
        })
      }

      return response.isSuccessful && response.payload ? response.payload : false
    } catch {
      await AppModule.setIsRequestActive(false)
      if (verbose) {
        toasts.error((i18n.t('userNotifications.toasts.updateUserNotificationsError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }
      return null
    }
  }

  @Action
  async archiveNotifications(
    payload: { userNotificationIds: number[], isArchived: boolean, verbose?: boolean})
    : Promise<boolean | null> {
    const verbose = payload.verbose ?? true
    try {
      await AppModule.setIsRequestActive(true)
      const response = await UserNotifications.archiveNotifications(
        payload.userNotificationIds,
        payload.isArchived
      )
      await AppModule.setIsRequestActive(false)

      if ((!response.isSuccessful || !response.payload) && verbose) {
        toasts.error((i18n.t('userNotifications.toasts.updateUserNotificationsError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }

      if (response.isSuccessful && verbose) {
        toasts.success((i18n.t('userNotifications.toasts.updateUserNotificationsSuccessfully')) as string, {
          title: (i18n.t('userNotifications.toasts.successTitle')) as string
        })
      }

      return response.isSuccessful && response.payload ? response.payload : false
    } catch {
      await AppModule.setIsRequestActive(false)
      if (verbose) {
        toasts.error((i18n.t('userNotifications.toasts.updateUserNotificationsError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }
      return null
    }
  }

  @Action
  async hasNewNotifications(
    payload: { since: Date })
    : Promise<boolean | null> {
    try {
      const response = await UserNotifications.hasNewNotifications(
        payload.since
      )

      return response.isSuccessful && response.payload ? response.payload : false
    } catch {
      return null
    }
  }

  @Action
  async getFilterEntities(
    payload: { verbose: boolean })
    : Promise<string[] | false | null> {
    const verbose = payload.verbose ?? true
    try {
      await AppModule.setIsRequestActive(true)
      const response = await UserNotifications.getFilterEntities()
      await AppModule.setIsRequestActive(false)

      if (!response.isSuccessful && verbose) {
        toasts.error((i18n.t('userNotifications.toasts.getFilterEntitiesError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }

      return response.isSuccessful && response.payload ? response.payload : false
    } catch {
      await AppModule.setIsRequestActive(false)
      if (verbose) {
        toasts.error((i18n.t('userNotifications.toasts.getFilterEntitiesServerError')) as string, {
          title: (i18n.t('userNotifications.toasts.errorTitle')) as string
        })
      }
      return null
    }
  }
}

export const UserNotificationsActionsModule = getModule(UserNotificationsActions)
